import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { HStack, Box, Text, Button, Input } from '@chakra-ui/react';
import { toast } from "react-toastify";
import Select from 'react-select';
import { Formik } from 'formik';
import { fetchWrapper } from '../../helpers';
import {  } from '../../components/form';
import API from '../../helpers/axiosInstance';

const VariableApi = () => {
  const [formData, setFormData] = useState({
    template: '',
    message: '',
    email: '',
    type: '1',
    channel: '',
    variables: [],
  });

  // Separate state for phone number
  const [phoneNumber, setPhoneNumber] = useState('');

  // Kept in memory only — never localStorage, so the key isn't readable by any
  // other script/tab or left behind after the page closes.
  const [apiKey, setApiKey] = useState('');

  const FormSchema = Yup.object().shape({
    template: Yup.string().required('Template is required'),
    to: Yup.string()
      .matches(/^[0-9+\-\s()]+$/, 'Phone number can only contain numbers and special characters')
      .required('Phone number is required'),
    variables: Yup.array().of(
      Yup.object().shape({
        key: Yup.string(),
        value: Yup.string().required('Value is required')
      })
    ),
    email: Yup.string()
      .email('Invalid email format')
      .nullable()
      .test('conditional-email', 'Email is required for this template', function (value) {
        const { template } = this.parent;
        const requiresEmail = false; // customize if needed
        return !requiresEmail || !!value;
      }),
    channel: Yup.string().required('Preferred channel is required'),
    // message: Yup.string().required('Message is required')
  });

  const [templateOptions, setTemplateOptions] = useState([]);
  const [channelOptions, setChannelOptions] = useState([]);
  const [slecetedTemplate, setSlecetedTemplate] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedTemplateMessage, setSelectedTemplateMessage] = useState('');
  const [selectedOption, setSelectedOption] = useState({});

  // Fallback for templates the backend didn't supply a `variables` list for —
  // scan the raw message text for $variable placeholders.
  const extractVariablesFromMessage = (message) => {
    const regex = /\$([a-zA-Z_]\w*)/g;
    const matches = (message || '').match(regex) || [];
    return matches.map(match => ({ key: match.substring(1), value: '' }));
  };

  const TemplateList = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/template/list`;
      const data = await fetchWrapper.get(url);
      let transformedData = (data.data || []).map((item) => ({
        label: item?.name,
        value: item?.name,
        ...item
      }));
      setTemplateOptions(transformedData);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast('Error fetching templates', { type: 'error' });
    }
  };

  const ChannelList = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/channel/list`;
      const data = await fetchWrapper.get(url);
      let transformedData = data.data.map((item) => ({
        label: item?.name,
        value: item?.name
      }));
      setChannelOptions([
        ...transformedData,
        { label: "Smart Routing", value: "smart" },
        { label: "Custom Routing", value: "custom" }
      ]);
    } catch (error) {
      console.error('Error fetching channels:', error);
    }
  };

  useEffect(() => {
    TemplateList();
    ChannelList();
    APIKEYGET();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectChange = (field, option) => {
    setSelectedOption(option);

    if (field === 'template' && option) {
      const selectedTemplate = templateOptions.find(t => t.value === option.value);
      if (selectedTemplate) {
        setSelectedTemplateMessage(selectedTemplate.message);
        setSlecetedTemplate(selectedTemplate);

        const baseVariables = selectedTemplate.variables?.length > 0
          ? selectedTemplate.variables.map(v => ({ key: v.name, value: '' })).filter(v => v.key)
          : extractVariablesFromMessage(selectedTemplate.message);

        // IVR templates can carry extra variables beyond the standard list (e.g. a
        // voice prompt variable) — merge them in, skipping anything already present.
        // Some entries also come back prefixed ("variables.discount" rather than
        // "discount") — strip that so the row label matches the $token actually
        // used in the message, and so it dedupes correctly against the standard
        // list (and against itself, in case two prefixed/unprefixed forms of the
        // same name both show up).
        const seenKeys = new Set(baseVariables.map(v => v.key));
        const ivrVars = selectedTemplate.is_ivr
          ? (selectedTemplate.ivr_variables || [])
              .map(key => String(key || '').replace(/^variables\./, ''))
              .filter(key => {
                if (!key || seenKeys.has(key)) return false;
                seenKeys.add(key);
                return true;
              })
              .map(key => ({ key, value: '' }))
          : [];
        const mergedVariables = [...baseVariables, ...ivrVars];

        setFormData(prev => ({
          ...prev,
          [field]: option.value,
          message: selectedTemplate.message,
          variables: mergedVariables
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: option ? option.value : ''
      }));
    }
  };

  const handleVariableChange = (index, field, value) => {
    const updatedVariables = formData.variables.map((variable, i) =>
      i === index ? { ...variable, [field]: value } : variable
    );

    let updatedMessage = selectedTemplateMessage;
    updatedVariables.forEach(variable => {
      if (variable.value) {
        updatedMessage = updatedMessage.replace(
          `$${variable.key}`,
          variable.value
        );
      }
    });

    setFormData(prev => ({
      ...prev,
      variables: updatedVariables,
      message: updatedMessage
    }));
  };

  const onSubmit = async (values, { setSubmitting }) => {
    const variablesObject = {};
    formData.variables.forEach(variable => {
      if (variable.key && variable.value) {
        variablesObject[variable.key] = variable.value;
      }
    });

    const payloadBase = {
      to: phoneNumber,
      email: formData.email,
      message_template_id: slecetedTemplate?.id,
      ...variablesObject,
    };

    const payload =
      selectedOption?.value === "custom" || selectedOption?.value === "smart"
        ? payloadBase
        : { ...payloadBase, preferred_channel: formData.channel };

    try {
      setLoading(true);
      const url = `${process.env.REACT_APP_SEND_API_URL}/v2/send`;
      await API.post(url, payload, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
          'host': "xnotify.ai"
        }
      });

      toast('Message has been sent', { type: 'info' });
    } catch (error) {
      console.error('Error:', error);
      toast(error?.response?.data?.message || 'Error sending message', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const APIKEYGET = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/customer/get-key?key_type=api_key`;
      const data = await fetchWrapper.get(url);
      setApiKey(data?.key || '');
    } catch (error) {
      console.error('Error fetching API key:', error);
    }
  };

  return (
    <div className="sms_body_section">
      <HStack justifyContent="space-between">
        <Box width="100%">
          <Formik
            enableReinitialize
            validationSchema={FormSchema}
            initialValues={{ ...formData, to: phoneNumber }}
            onSubmit={onSubmit}
          >
            {({ errors, touched, handleSubmit }) => (
              <form onSubmit={handleSubmit}>
                {/* Template Select */}
                <Box mb="10px">
                  <Text fontSize="16px" fontWeight="400" mb="5px">
                    Message Template <span style={{ color: "red" }}>*</span>
                  </Text>
                  <Select
                    options={templateOptions}
                    value={templateOptions.find(option => option.value === formData.template)}
                    onChange={(option) => handleSelectChange('template', option)}
                    placeholder="Select Template"
                    className={errors.template && touched.template ? 'is-invalid' : ''}
                  />
                  {errors.template && touched.template && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.template}
                    </Text>
                  )}
                </Box>

                {/* Variables */}
                {formData.variables.length > 0 && (
                  <>
                    <Text fontSize="16px" fontWeight={400}>
                      Variables <span style={{ color: "red" }}>*</span>
                    </Text>
                    {formData.variables.map((variable, index) => (
                      <HStack key={index} w="100%" mt="5px" alignItems="baseline">
                        <Box w="50%">
                          <Input
                            value={variable.key}
                            isReadOnly
                            placeholder="Variable"
                            bg="gray.100"
                          />
                        </Box>
                        <Box w="50%">
                          <Input
                            value={variable.value}
                            onChange={(e) =>
                              handleVariableChange(index, 'value', e.target.value)
                            }
                            placeholder="Value"
                          />
                          {errors.variables?.[index]?.value &&
                            touched.variables?.[index]?.value && (
                              <Text color="red.500" fontSize="sm" mt={1}>
                                {errors.variables[index].value}
                              </Text>
                            )}
                        </Box>
                      </HStack>
                    ))}
                  </>
                )}

                {/* Channel */}
                <Box mb="10px" mt="10px">
                  <Text fontSize="16px" fontWeight={400} mb="5px">
                    Preferred Channel <span style={{ color: "red" }}>*</span>
                  </Text>
                  <Select
                    options={channelOptions}
                    value={channelOptions.find(option => option.value === formData.channel)}
                    onChange={(option) => handleSelectChange('channel', option)}
                    placeholder="Select Channel"
                  />
                </Box>

                {/* Phone Number (separate state) */}
                <Box mb="10px" mt="10px">
                  <Text fontSize="16px" fontWeight={400} mb="5px">
                    Mobile Number <span style={{ color: "red" }}>*</span>
                  </Text>
                  <Input
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="Enter Phone Number starting with 92 or country code"
                  />
                  {errors.to && touched.to && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.to}
                    </Text>
                  )}
                </Box>
                
                <Box mb="10px" mt="10px">
                  <Text fontSize="16px" fontWeight="400" mb="5px">
                    E-Mail (Optional)
                  </Text>
                  <Input
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="Please Enter Email ID"
                    type="email"
                    bg="white"
                  />
                  {errors.email && touched.email && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.email}
                    </Text>
                  )}
                </Box>


                {/* Submit */}
                <Button
                  isLoading={loading}
                  py="10px"
                  px="25px"
                  color="white"
                  background={"var(--brand-primary)"}
                  fontSize="14px"
                  fontWeight="500"
                  fontFamily="'Lato', sans-serif"
                  mb={4}
                  style={{ width: "100%" }}
                  type="submit"
                >
                  Send Test Message
                </Button>
              </form>
            )}
          </Formik>
        </Box>
      </HStack>
    </div>
  );
};

export default VariableApi;
